/* Reports engine (ported from the prototype): filter -> enrich with money -> group -> drill down.
   Revenue = billable hours x project rate (or the person's billing rate when the project has none; $0 stays $0);
   cost = hours x the member's cost rate in effect on that day. */
import {costAt, projRate, type Data, type Entry, type Member, type Project, type Client, type Task} from './model.ts';
import {isoDate, todayStr, addDays, daysBetween, weekStart, monthEnd, wdOf} from './time.ts';

export const NONE = '__none__';
export const RPT_DIMS: Record<string, string> = {client: 'Client', project: 'Project', task: 'Task', member: 'Team member', day: 'Day', week: 'Week', month: 'Month', tag: 'Tag', billable: 'Billable'};
export const RPT_PRESETS: [string, string][] = [['all', 'All time'], ['today', 'Today'], ['yesterday', 'Yesterday'], ['thisWeek', 'This week'], ['lastWeek', 'Last week'], ['last7', 'Last 7 days'], ['last30', 'Last 30 days'],
  ['thisMonth', 'This month'], ['lastMonth', 'Last month'], ['thisQuarter', 'This quarter'], ['lastQuarter', 'Last quarter'], ['thisYear', 'This year'], ['lastYear', 'Last year'], ['custom', 'Custom range…']];
export const RPT_TEMPLATES: [string, string[]][] = [
  ['By client', ['client', 'project', 'task']],
  ['By team member', ['member', 'client', 'project']],
  ['Timesheet', ['member', 'day']],
  ['Monthly', ['month', 'client', 'project']],
  ['Billable split', ['billable', 'client']],
  ['Who works on what', ['client', 'member']],
];

export interface Filters {
  preset: string; from: string; to: string;
  members: string[]; clients: string[]; projects: string[]; tasks: string[]; tags: string[];
  billable: 'all' | 'billable' | 'nonbillable'; minHours: string; q: string; path: string[]; compare: boolean;
  view: 'summary' | 'detailed'; slice: string; sort: string; asc: boolean;   // Reports tab, ring chart's grouping, Detailed's sort
  status: string[];   // which clients/projects/tasks/people the filter menus list: 'active', 'inactive', 'archived'
}
export function defaultFilters(): Filters {
  return {preset: 'all', from: '', to: '', members: [], clients: [], projects: [], tasks: [], tags: [], billable: 'all', minHours: '', q: '', path: ['client', 'project', 'task'], compare: false,
          view: 'summary', slice: 'client', sort: 'start', asc: false, status: ['active']};
}

/** date range for a preset, in the workspace zone ("today" is Chicago's today whatever the browser's zone) */
export function rptRange(preset: string, today = todayStr()): [string, string] {
  const t = today.split('-').map(Number), now = new Date(t[0], t[1] - 1, t[2]), y = now.getFullYear(), m = now.getMonth(), d = now.getDate(), dow = (now.getDay() + 6) % 7, q = Math.floor(m / 3) * 3;
  const D = (yy: number, mm: number, dd: number) => isoDate(new Date(yy, mm, dd));
  switch (preset) {
    case 'today': return [D(y, m, d), D(y, m, d)];
    case 'yesterday': return [D(y, m, d - 1), D(y, m, d - 1)];
    case 'thisWeek': return [D(y, m, d - dow), D(y, m, d - dow + 6)];
    case 'lastWeek': return [D(y, m, d - dow - 7), D(y, m, d - dow - 1)];
    case 'last7': return [D(y, m, d - 6), D(y, m, d)];
    case 'last30': return [D(y, m, d - 29), D(y, m, d)];
    case 'thisMonth': return [D(y, m, 1), D(y, m + 1, 0)];
    case 'lastMonth': return [D(y, m - 1, 1), D(y, m, 0)];
    case 'thisQuarter': return [D(y, q, 1), D(y, q + 3, 0)];
    case 'lastQuarter': return [D(y, q - 3, 1), D(y, q, 0)];
    case 'thisYear': return [D(y, 0, 1), D(y, 11, 31)];
    case 'lastYear': return [D(y - 1, 0, 1), D(y - 1, 11, 31)];
  }
  const ym = /^year:(\d{4})$/.exec(String(preset));
  if (ym) { const yy = +ym[1]; return [D(yy, 0, 1), D(yy, 11, 31)]; }
  return ['', ''];
}
/** Previous period: whole month -> previous month, whole quarter -> previous quarter, whole year -> previous year,
    anything else -> the same number of days immediately before. */
export function rptPrevRange(from: string, to: string): [string, string] {
  const [y, m, d] = from.split('-').map(Number);
  const mk = (yy: number, mm: number, dd: number) => isoDate(new Date(yy, mm - 1, dd));
  if (d === 1) {
    if (m === 1 && to === mk(y, 12, 31)) return [mk(y - 1, 1, 1), mk(y - 1, 12, 31)];
    if ((m - 1) % 3 === 0 && to === mk(y, m + 3, 0)) return [mk(y, m - 3, 1), mk(y, m, 0)];
    if (to === mk(y, m + 1, 0)) return [mk(y, m - 1, 1), mk(y, m, 0)];
  }
  const len = daysBetween(from, to) + 1, pTo = addDays(from, -1);
  return [addDays(pTo, -(len - 1)), pTo];
}
/** past years that contain data, newest first (one preset each) */
export function pastYears(entries: Entry[], today = todayStr()): number[] {
  const thisYear = +today.slice(0, 4), s = new Set<number>();
  for (const e of entries) { const yy = +String(e.date || '').slice(0, 4); if (yy && yy < thisYear) s.add(yy); }
  return [...s].sort((a, b) => b - a);
}

export interface Ctx { proj: Record<string, Project>; cli: Record<string, Client>; mem: Record<string, Member>; task: Record<string, Task> }
export function rptCtx(d: Pick<Data, 'projects' | 'clients' | 'members' | 'tasks'>): Ctx {
  const c: Ctx = {proj: {}, cli: {}, mem: {}, task: {}};
  d.projects.forEach(p => c.proj[p.id] = p); d.clients.forEach(x => c.cli[x.id] = x);
  d.members.forEach(m => c.mem[m.id] = m); d.tasks.forEach(t => c.task[t.id] = t);
  return c;
}
/* Money is summed exactly, in whole "seconds x cents" units (rates have at most 2 decimals), and turned into dollars
   rounded to the cent only at the end — the same result as the database's exact numeric arithmetic. Plain floating
   point sums can land a half-cent total on the wrong side (seen on the real data: 2019 cost off by $0.01). */
/** One entry, or (from the server) a group of entries with the same keys: n = how many entries it stands for. */
/** costNone = seconds whose cost is unknown (the person has no cost rate): left out of cost, never counted as $0 */
export interface Item { e: Entry; sec: number; bill: number; revU: number; costU: number; costNone?: number; clientId: string; projectId: string; taskId: string; memberId: string; n?: number }
const cents = (rate: number) => Math.round(rate * 100);
export const dollars = (u: number) => Math.round(u / 3600) / 100;   // seconds x cents -> dollars, rounded to the cent
export function rptEnrich(entries: Entry[], ctx: Ctx): Item[] {
  return entries.map(e => {
    const p = ctx.proj[e.projectId], m = ctx.mem[e.memberId], sec = e.durationSeconds || 0;
    const c = m ? costAt(m, e.date) : 0;
    return {e, sec, bill: e.billable ? sec : 0, revU: (e.billable && p) ? sec * cents(projRate(p, m)) : 0, costU: c === null ? 0 : sec * cents(c), costNone: c === null ? sec : 0,
            clientId: p ? p.clientId : '', projectId: e.projectId || '', taskId: e.taskId || '', memberId: e.memberId};
  });
}
export function rptPredicate(f: Filters, ctx: Ctx, useDates: boolean, from: string, to: string): (e: Entry) => boolean {
  const q = f.q.trim().toLowerCase();
  const mem = new Set(f.members), cl = new Set(f.clients), pr = new Set(f.projects), tk = new Set(f.tasks), tg = new Set(f.tags);
  const minSec = f.minHours ? parseFloat(f.minHours) * 3600 : 0;
  return (e) => {
    if (useDates) { if (from && e.date < from) return false; if (to && e.date > to) return false; }
    if (mem.size && !mem.has(e.memberId)) return false;
    const p = ctx.proj[e.projectId];
    if (cl.size && !cl.has(p ? p.clientId : NONE)) return false;
    if (pr.size && !pr.has(e.projectId || NONE)) return false;
    if (tk.size && !tk.has(e.taskId || NONE)) return false;
    if (tg.size) { const t = e.tags || []; if (!(t.length ? t.some(x => tg.has(x)) : tg.has(NONE))) return false; }
    if (f.billable === 'billable' && !e.billable) return false;
    if (f.billable === 'nonbillable' && e.billable) return false;
    if (minSec && (e.durationSeconds || 0) <= minSec) return false;
    if (q) {
      const c = p ? ctx.cli[p.clientId] : null, t = ctx.task[e.taskId], m = ctx.mem[e.memberId];
      const hay = [e.description, c && c.name, p && p.name, t && t.name, m && m.name, (e.tags || []).join(' ')].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
}
/** costNone: seconds whose cost is unknown (left out of cost and margin; see costKnown) */
export interface Metrics { sec: number; bill: number; rev: number; cost: number; n: number; costNone?: number }
export function rptMetrics(items: Item[]): Metrics {
  let sec = 0, bill = 0, revU = 0, costU = 0, costNone = 0;
  let n = 0;
  for (const x of items) { sec += x.sec; bill += x.bill; revU += x.revU; costU += x.costU; costNone += x.costNone || 0; n += x.n ?? 1; }
  return {sec, bill, rev: dollars(revU), cost: dollars(costU), n, costNone};
}
/** 'all' = every second has a known cost; 'none' = no second does (cost and margin are unknown, not $0); 'some' = partly */
export function costKnown(g: Metrics): 'all' | 'some' | 'none' {
  const u = g.costNone || 0;
  return u <= 0 ? 'all' : u >= g.sec ? 'none' : 'some';
}
export interface Group extends Metrics { key: string; label: string; sub: string; dim: string; items: Item[] }
export function rptDimKeys(x: Item, dim: string, ctx: Ctx): {k: string; l: string; sub?: string}[] {
  switch (dim) {
    case 'client': { const c = ctx.cli[x.clientId]; return [{k: x.clientId || NONE, l: c ? c.name : (x.clientId ? '(unknown client)' : '(No client)')}]; }
    case 'project': { const p = ctx.proj[x.projectId], c = p ? ctx.cli[p.clientId] : null; return [{k: x.projectId || NONE, l: p ? p.name : '(No project)', sub: c ? c.name : ''}]; }
    case 'task': { const t = ctx.task[x.taskId], p = ctx.proj[x.projectId]; return [{k: x.taskId || NONE, l: t ? t.name : '(No task)', sub: t && p ? p.name : ''}]; }
    case 'member': { const m = ctx.mem[x.memberId]; return [{k: x.memberId || NONE, l: m ? m.name : '(unknown member)'}]; }
    case 'day': return [{k: x.e.date, l: x.e.date + ' ' + wdOf(x.e.date)}];
    case 'week': { const w = weekStart(x.e.date); return [{k: w, l: 'Week of ' + w}]; }
    case 'month': { const mth = x.e.date.slice(0, 7); return [{k: mth, l: mth}]; }
    case 'tag': { const t = x.e.tags || []; return t.length ? t.map(v => ({k: v, l: v})) : [{k: NONE, l: '(No tag)'}]; }
    case 'billable': return [x.e.billable ? {k: 'billable', l: 'Billable'} : {k: 'nonbillable', l: 'Non-billable'}];
  }
  return [{k: NONE, l: '?'}];
}
export function rptGroup(items: Item[], dim: string, ctx: Ctx): Group[] {
  const map = new Map<string, Group>();
  items.forEach(x => rptDimKeys(x, dim, ctx).forEach(({k, l, sub}) => {
    let g = map.get(k);
    if (!g) { g = {key: k, label: l, sub: sub || '', dim, items: [], sec: 0, bill: 0, rev: 0, cost: 0, n: 0, costNone: 0}; map.set(k, g); }
    g.items.push(x);
  }));
  const arr = [...map.values()];
  arr.forEach(g => Object.assign(g, rptMetrics(g.items)));
  const chrono = dim === 'day' || dim === 'week' || dim === 'month';
  arr.sort(chrono ? (a, b) => a.key < b.key ? -1 : 1 : (a, b) => b.sec - a.sec);
  return arr;
}
/* ---------- Summary below the chosen levels (owner decision 2026-10-28): the deepest level opens into Task subtotals
   (unless Task is already one of the levels), and a task into one line per Description. Never individual entries. ---------- */
/** entries from the server (report_entries) as items with the server's exact money units, so subtotals add up like its groups */
export function itemsFromRows(rows: {e: Entry; rev_u: number | null; cost_u: number | null}[], ctx: Ctx): Item[] {
  return rows.map(({e, rev_u, cost_u}) => {
    const sec = e.durationSeconds || 0;
    return {e, sec, bill: e.billable ? sec : 0, revU: Number(rev_u || 0), costU: Number(cost_u || 0), costNone: rev_u != null && cost_u == null ? sec : 0,
            clientId: ctx.proj[e.projectId]?.clientId || '', projectId: e.projectId || '', taskId: e.taskId || '', memberId: e.memberId, n: 1};
  });
}
/** the same description = the same text once the spaces around it are taken off (case and inner spaces count) */
export const descKey = (d: string | undefined) => (d || '').trim();
export interface DescGroup extends Group { first: string; last: string }
/** one subtotal per description (largest first; "(no description)" for empty), with the first and last day it was used */
export function rptDescGroups(items: Item[]): DescGroup[] {
  const map = new Map<string, DescGroup>();
  for (const x of items) {
    const k = descKey(x.e.description);
    let g = map.get(k);
    if (!g) { g = {key: k, label: k || '(no description)', sub: '', dim: 'description', items: [], sec: 0, bill: 0, rev: 0, cost: 0, n: 0, costNone: 0, first: x.e.date, last: x.e.date}; map.set(k, g); }
    g.items.push(x);
    if (x.e.date < g.first) g.first = x.e.date;
    if (x.e.date > g.last) g.last = x.e.date;
  }
  const arr = [...map.values()];
  arr.forEach(g => Object.assign(g, rptMetrics(g.items)));
  return arr.sort((a, b) => b.sec - a.sec || a.label.localeCompare(b.label));
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Sep 17 – Sep 30", "Sep 30" for one day; the years are added when the span crosses a year */
export function dateSpan(a: string, b: string): string {
  if (!a || !b) return '';
  const d = (s: string, y: boolean) => MON[+s.slice(5, 7) - 1] + ' ' + (+s.slice(8, 10)) + (y ? ', ' + s.slice(0, 4) : '');
  if (a === b) return d(a, false);
  const y = a.slice(0, 4) !== b.slice(0, 4);
  return d(a, y) + ' – ' + d(b, y);
}
export function rptWalk(items: Item[], path: string[], level: number, parentKey: string, ctx: Ctx, fn: (g: Group, level: number, key: string) => void) {
  rptGroup(items, path[level], ctx).forEach(g => {
    const nk = parentKey + '␟' + g.dim + ':' + g.key; fn(g, level, nk);
    if (level + 1 < path.length) rptWalk(g.items, path, level + 1, nk, ctx, fn);
  });
}
export interface Bucket { key: string; start: string; end: string; label: string; sec: number; bill: number; n: number }
/** the empty bars for a date range: by day up to 45 days, by week up to 200 days, by month up to about 3 years, else by year
    (the server sends months for anything over 200 days; they add up into years here) */
function bucketFrame(lo: string, hi: string) {
  const span = daysBetween(lo, hi) + 1;
  const unit = span <= 45 ? 'day' : span <= 200 ? 'week' : span <= 1100 ? 'month' : 'year';
  const keyOf = unit === 'day' ? ((d: string) => d) : unit === 'week' ? weekStart : unit === 'month' ? ((d: string) => d.slice(0, 7)) : ((d: string) => d.slice(0, 4));
  const buckets: Bucket[] = [], idx: Record<string, number> = {};
  let cur = unit === 'day' ? lo : unit === 'week' ? weekStart(lo) : unit === 'month' ? lo.slice(0, 7) + '-01' : lo.slice(0, 4) + '-01-01';
  while (cur <= hi && buckets.length < 400) {
    const k = keyOf(cur);
    const end = unit === 'day' ? cur : unit === 'week' ? addDays(cur, 6) : unit === 'month' ? monthEnd(cur.slice(0, 7)) : cur.slice(0, 4) + '-12-31';
    idx[k] = buckets.length;
    buckets.push({key: k, start: cur < lo ? lo : cur, end: end > hi ? hi : end, label: unit === 'day' ? cur + ' ' + wdOf(cur) : unit === 'week' ? 'Week of ' + cur : unit === 'month' ? MONTHS[+cur.slice(5, 7) - 1] + ' ' + cur.slice(0, 4) : cur.slice(0, 4), sec: 0, bill: 0, n: 0});
    cur = unit === 'day' ? addDays(cur, 1) : unit === 'week' ? addDays(cur, 7) : unit === 'month' ? isoDate(new Date(+cur.slice(0, 4), +cur.slice(5, 7), 1)) : (+cur.slice(0, 4) + 1) + '-01-01';
  }
  return {unit, keyOf, buckets, idx};
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** the two short lines under a trend bar: "Thu" / "10/01", "Week" / "10/06", "Oct" / "2026", "2026" */
export function axisLabel(unit: string, key: string): [string, string] {
  if (unit === 'day') return [wdOf(key), key.slice(5, 7) + '/' + key.slice(8, 10)];
  if (unit === 'week') return ['Wk', key.slice(5, 7) + '/' + key.slice(8, 10)];
  if (unit === 'month') return [MONTHS[+key.slice(5, 7) - 1], key.slice(0, 4)];
  return [key.slice(0, 4), ''];
}
export function rptBuckets(items: Item[], from: string, to: string): {unit: string; buckets: Bucket[]} | null {
  if (!items.length) return null;
  let lo = from, hi = to;
  if (!lo || !hi) { let mn = items[0].e.date, mx = items[0].e.date; items.forEach(x => { if (x.e.date < mn) mn = x.e.date; if (x.e.date > mx) mx = x.e.date; }); lo = lo || mn; hi = hi || mx; }
  if (hi < lo) return null;
  const fr = bucketFrame(lo, hi);
  items.forEach(x => { const i = fr.idx[fr.keyOf(x.e.date)]; if (i != null) { fr.buckets[i].sec += x.sec; fr.buckets[i].bill += x.bill; fr.buckets[i].n += x.n ?? 1; } });
  return {unit: fr.unit, buckets: fr.buckets};
}

/* ---------- reports computed by the database (public.report): its rows become Items the screen groups as before ---------- */
export interface ServerGroup { c?: string; p?: string; k?: string; m?: string; d?: string; b?: boolean; g?: string; sec: number; bill: number; rev_u: number | null; cost_u: number | null; n: number; cost_none?: number | null }
export interface ServerTotal { sec: number; bill: number; rev_u: number | null; cost_u: number | null; n: number; days: number; min: string | null; max: string | null; cost_none?: number | null }
const real = (v: string | undefined) => v && v !== NONE ? v : '';
export function itemsFromServer(groups: ServerGroup[]): Item[] {
  return groups.map(g => ({
    e: {id: '', memberId: g.m || '', projectId: real(g.p), taskId: real(g.k), description: '', billable: !!g.b, date: g.d || '', startedAt: '', endedAt: '', so: 0,
        durationSeconds: g.sec, tags: g.g && g.g !== NONE ? [g.g] : [], source: ''},
    sec: g.sec, bill: g.bill, revU: Number(g.rev_u || 0), costU: Number(g.cost_u || 0), costNone: Number(g.cost_none || 0), n: g.n,
    clientId: real(g.c), projectId: real(g.p), taskId: real(g.k), memberId: g.m || '',
  }));
}
export const totalFromServer = (t: ServerTotal): Metrics => ({sec: t.sec, bill: t.bill, rev: dollars(Number(t.rev_u || 0)), cost: dollars(Number(t.cost_u || 0)), n: t.n, costNone: Number(t.cost_none || 0)});
/** the trend bars from the database's per-period sums (dates of the range, or of the data when the range is open) */
export function bucketsFromServer(rows: {d: string; sec: number; bill: number; n: number}[], from: string, to: string, t: ServerTotal): {unit: string; buckets: Bucket[]} | null {
  const lo = from || t.min, hi = to || t.max;
  if (!t.n || !lo || !hi || hi < lo) return null;
  const fr = bucketFrame(lo, hi);
  rows.forEach(r => { const i = fr.idx[fr.keyOf(r.d)]; if (i != null) { fr.buckets[i].sec += r.sec; fr.buckets[i].bill += r.bill; fr.buckets[i].n += r.n; } });
  return {unit: fr.unit, buckets: fr.buckets};
}
